"use client";
import React, { createContext, useCallback, useEffect, useState } from "react";
import {
  BrandWithModelsFilter,
  DriveWheelFilter,
  FuelFilter,
  RangeFilter,
  DepartmentsFilter,
  EquipmentFilter,
  SalesTypeFilter,
  ColorFilter,
  AvailabilityFilter,
} from "./types/filters.types";
import {
  activateBrandsWithModelsFromQuery,
  activateColorFromQuery,
  activateDepartmentsFromQuery,
  activateDriveWheelFromQuery,
  activateEquipmentFromQuery,
  activateFuelFromQuery,
  activateSalesTypesFromQuery,
  activateAvailabilityFromQuery,
  setRangeFilterFromQuery,
} from "./utils/queryParamFilterUtils";
import { useSearchParams } from "next/navigation";

export interface CarFilterContext {
  brandsWithModels: BrandWithModelsFilter[] | undefined;
  setBrandsWithModels: (x: BrandWithModelsFilter[]) => void;
  fuelFilter: FuelFilter[] | undefined;
  setFuelFilter: (x: FuelFilter[]) => void;
  driveWheelFilter: DriveWheelFilter[] | undefined;
  setDriveWheelFilter: (x: DriveWheelFilter[]) => void;
  priceRange: RangeFilter | undefined;
  setPriceRange: (x: RangeFilter | undefined) => void;
  modelYearRange: RangeFilter | undefined;
  setModelYearRange: (x: RangeFilter | undefined) => void;
  mileageRange: RangeFilter | undefined;
  setMileageRange: (x: RangeFilter | undefined) => void;
  departmentsFilter: DepartmentsFilter[] | undefined;
  setDepartmentsFilter: (x: DepartmentsFilter[]) => void;
  equipmentFilter: EquipmentFilter[] | undefined;
  setEquipmentFilter: (x: EquipmentFilter[]) => void;
  salesTypeFilter: SalesTypeFilter[] | undefined;
  setSalesTypeFilter: (x: SalesTypeFilter[]) => void;
  colorFilter: ColorFilter[] | undefined;
  setColorFilter: (x: ColorFilter[]) => void;
  availabilityFilter: AvailabilityFilter[] | undefined;
  setAvailabilityFilter: (x: AvailabilityFilter[]) => void;

}

const carFilterContextDefaultVal: CarFilterContext = {
  brandsWithModels: [],
  setBrandsWithModels: () => { },
  fuelFilter: [],
  setFuelFilter: () => { },
  driveWheelFilter: [],
  setDriveWheelFilter: () => { },
  priceRange: undefined,
  setPriceRange: () => { },
  modelYearRange: undefined,
  setModelYearRange: () => { },
  mileageRange: undefined,
  setMileageRange: () => { },
  departmentsFilter: [],
  setDepartmentsFilter: () => { },
  equipmentFilter: [],
  setEquipmentFilter: () => { },
  salesTypeFilter: [],
  setSalesTypeFilter: () => { },
  colorFilter: [],
  setColorFilter: () => { },
  availabilityFilter: [],
  setAvailabilityFilter: () => { }
};

export const FilterContext = createContext(carFilterContextDefaultVal);

interface FiltersProvider {
  fetchedBrandsWithModels: BrandWithModelsFilter[];
  fetchedFuels: FuelFilter[];
  fetchedDriveWheels: DriveWheelFilter[];
  fetchedEquipment: EquipmentFilter[];
  departments?: DepartmentsFilter[];
  salesTypes: SalesTypeFilter[];
  colors: ColorFilter[];
  availabilityOptions: AvailabilityFilter[];
  children: React.ReactNode;
}
export const FiltersProvider = ({
  fetchedBrandsWithModels,
  fetchedFuels,
  fetchedDriveWheels,
  fetchedEquipment,
  departments,
  salesTypes,
  colors,
  availabilityOptions,
  children,
}: FiltersProvider) => {

  const [brandsWithModels, setBrandsWithModels] = useState<BrandWithModelsFilter[]>(fetchedBrandsWithModels);
  const [fuelFilter, setFuelFilter] = useState<FuelFilter[]>(fetchedFuels);
  const [colorFilter, setColorFilter] = useState<ColorFilter[]>(colors ?? []);
  const [driveWheelFilter, setDriveWheelFilter] = useState<DriveWheelFilter[]>(fetchedDriveWheels);
  const [priceRange, setPriceRange] = useState<RangeFilter>();
  const [modelYearRange, setModelYearRange] = useState<RangeFilter>();
  const [mileageRange, setMileageRange] = useState<RangeFilter>();
  const [departmentsFilter, setDepartmentsFilter] = useState<DepartmentsFilter[]>(departments || []);
  const [equipmentFilter, setEquipmentFilter] = useState<EquipmentFilter[]>(fetchedEquipment);
  const [salesTypeFilter, setSalesTypeFilter] = useState<SalesTypeFilter[]>(salesTypes);
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter[]>(availabilityOptions);

  const searchParams = useSearchParams();

  const activateFiltersFromSearchParams = useCallback(() => {
    setBrandsWithModels((b) =>
      activateBrandsWithModelsFromQuery(b, searchParams),
    );
    setFuelFilter((f) => activateFuelFromQuery(f, searchParams));

    setColorFilter((f) => activateColorFromQuery(f, searchParams));

    setDriveWheelFilter((f) => activateDriveWheelFromQuery(f, searchParams));

    setEquipmentFilter((f) => activateEquipmentFromQuery(f, searchParams));

    setSalesTypeFilter((f) => activateSalesTypesFromQuery(f, searchParams));

    setAvailabilityFilter((f) => activateAvailabilityFromQuery(f, searchParams));

    setPriceRange(
      setRangeFilterFromQuery(
        "priceStartRangeKey",
        "priceEndRangeKey",
        searchParams,
      ),
    );

    setModelYearRange(
      setRangeFilterFromQuery(
        "modelYearStartRangeKey",
        "modelYearEndRangeKey",
        searchParams,
      ),
    );

    setMileageRange(
      setRangeFilterFromQuery(
        "mileageStartRangeKey",
        "mileageEndRangeKey",
        searchParams,
      ),
    );

    setDepartmentsFilter((f) => activateDepartmentsFromQuery(f, searchParams));
  }, [searchParams]);

  useEffect(() => {
    activateFiltersFromSearchParams();
  }, [activateFiltersFromSearchParams, searchParams]);

  return (
    <FilterContext.Provider
      value={{
        brandsWithModels,
        setBrandsWithModels,
        fuelFilter,
        setFuelFilter,
        driveWheelFilter,
        setDriveWheelFilter,
        priceRange,
        setPriceRange,
        modelYearRange,
        setModelYearRange,
        mileageRange,
        setMileageRange,
        departmentsFilter,
        setDepartmentsFilter,
        equipmentFilter,
        setEquipmentFilter,
        salesTypeFilter,
        setSalesTypeFilter,
        colorFilter,
        setColorFilter,
        availabilityFilter,
        setAvailabilityFilter
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};
